import React from 'react';
import Breadcrumbs from '@grnhse/seedling/lib/birch/components/breadcrumbs';
import { SectionHeader } from '@grnhse/seedling/lib/birch/typography';
import { GlobalWhiteBackgroundImportant } from 'redesign_2022/global_white_background';
import TalentMatchingQueryClientProvider from 'talent_matching/talent_matching_query_client';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import FilterForm from 'talent_matching/components/filter_form';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  type UserContextValue,
  UserContextProvider,
} from 'talent_matching/contexts/user_context';
import { RankingInProgressProvider } from 'talent_matching/contexts/ranking_in_progress_context';
import { IndexingStatusProvider } from 'talent_matching/contexts/indexing_status_context';
import { BreakpointContextProvider } from 'talent_matching/contexts/breakpoint_context';
import { useDocumentTitle } from 'shared/utils/react_router_helper';
import { useLocalStorage } from 'react-use';
import { TalentMatchingFilters } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching');

type Props = {
  initialFilters: Partial<TalentMatchingFilters>;
  jobTitle: string;
  previouslyCalibrated: boolean;
  pollRankingOnLoad: boolean;
  pollIndexingOnLoad: boolean;
  appReviewSessionId: number;
} & UserContextValue;

const TalentMatchingPage = ({
  initialFilters,
  jobTitle,
  previouslyCalibrated,
  pollRankingOnLoad,
  pollIndexingOnLoad,
  appReviewSessionId,
  ...userContextProps
}: Props) => {
  useDocumentTitle(t('document_title'));
  const { paths } = userContextProps;

  const [activeSidePanelTab, setActiveSidePanelTab] = useLocalStorage<
    string | null
  >('talent_matching_candidate_panel_tab', null);

  const [savedSearchOptions, setSavedSearchOptions] = React.useState(
    userContextProps.savedSearchOptions
  );

  return (
    <TalentMatchingQueryClientProvider>
      <UserContextProvider
        value={{
          ...userContextProps,
          jobTitle,
          appReviewSessionId,
          activeSidePanelTab: activeSidePanelTab ?? null,
          setActiveSidePanelTab,
          savedSearchOptions,
          setSavedSearchOptions,
        }}
      >
        <IndexingStatusProvider pollOnLoad={pollIndexingOnLoad}>
          <RankingInProgressProvider
            pollOnLoad={pollRankingOnLoad}
            previouslyCalibrated={previouslyCalibrated}
          >
            <BreakpointContextProvider>
              <GlobalWhiteBackgroundImportant />
              <Stack spacing={1}>
                <Breadcrumbs
                  current={t('title')}
                  crumbs={[
                    {
                      label: t('breadcrumbs.all_jobs'),
                      href: paths.allJobsPath,
                    },
                    {
                      label: jobTitle,
                      href: paths.jobDashboardPath,
                    },
                  ]}
                />
                <SectionHeader large>{t('header')}</SectionHeader>
                <FilterForm initialFilters={initialFilters} />
              </Stack>
            </BreakpointContextProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </RankingInProgressProvider>
        </IndexingStatusProvider>
      </UserContextProvider>
    </TalentMatchingQueryClientProvider>
  );
};

export default TalentMatchingPage;
